import { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useOutlet, useSearchParams } from "react-router-dom";
import { ContainerStyles, TextStyles } from "styles/style";
import { utils } from "supabase/content/utils.ts";
import BaseView from "./BaseView";
import { api } from "supabase/database/api.ts";

export default function ListView({
  title = null,
  list = null,
  table = null,
  url = null,
}) {
  const [material, setMaterial] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 15;
  const [capLength, setCapLength] = useState(63);
  const hasChild = useOutlet();

  // Get page and search from URL params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentSearch = searchParams.get("search") || "";

  // Fetch all data once
  useEffect(() => {
    async function prepMaterial() {
      if (list) {
        setMaterial(list);
      } else if (table) {
        const data = await api.contentQueries.getContentList({ table });
        setMaterial(data);
      }
    }
    prepMaterial();
  }, [list, table]);

  // Handle search change
  const handleSearchChange = (newSearch) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (newSearch.trim()) {
          newParams.set("search", newSearch);
        } else {
          newParams.delete("search");
        }
        // Always reset to page 1 when searching
        if (newSearch.trim() !== currentSearch) {
          newParams.set("page", "1");
        }
        return newParams;
      },
      { replace: true },
    );
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", newPage.toString());
      return newParams;
    });
  };

  // Responsive title length
  useEffect(() => {
    const updateCapLength = () => {
      const width = window.innerWidth;
      if (width <= 640) setCapLength(63);
      else if (width <= 768) setCapLength(110);
      else setCapLength(180);
    };
    updateCapLength();
    window.addEventListener("resize", updateCapLength);
    return () => window.removeEventListener("resize", updateCapLength);
  }, []);

  // Filter + paginate in memory
  const filtered = useMemo(() => {
    return material.filter(
      (item) =>
        item.title?.toLowerCase().includes(currentSearch.toLowerCase()) ||
        item.date?.toLowerCase().includes(currentSearch.toLowerCase()),
    );
  }, [material, currentSearch]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  return (
    <>
      {hasChild ? (
        <Outlet />
      ) : (
        <BaseView
          title={title}
          listCount={filtered.length}
          search={currentSearch}
          onSearchChange={handleSearchChange}
          page={currentPage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
        >
          <ContainerStyles.Posts>
            {paginated.map((item, index) => (
              <ContainerStyles.PostContainer key={index} featured={true}>
                <TextStyles.Post
                  className="group"
                  as={Link}
                  to={`${item.path || item.id}`}
                >
                  <TextStyles.Info>
                    {item.date && (
                      <TextStyles.Category>
                        {utils.formattedDate(item.date)}
                      </TextStyles.Category>
                    )}
                    {item.region && item.region.toLowerCase() !== "all" && (
                      <TextStyles.RegionBadge>{item.region.toUpperCase()}</TextStyles.RegionBadge>
                    )}
                    <TextStyles.Title>
                      {item.title.length > capLength
                        ? item.title.slice(0, capLength) + "..."
                        : item.title}
                    </TextStyles.Title>
                  </TextStyles.Info>
                </TextStyles.Post>
              </ContainerStyles.PostContainer>
            ))}
          </ContainerStyles.Posts>
        </BaseView>
      )}
    </>
  );
}
